import React, { useState } from 'react'
import Top from './Top'
import VFooter from './VFooter'
import { Card } from './ui';
import { ChevronDown, ChevronRight, Shield, Lock, Users, Database, Mail, Phone } from 'lucide-react';

export default function PrivacyPolicy() {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const sections = [
    {
      title: "Information We Collect",
      icon: <Database className="w-5 h-5" />,
      content: [
        {
          subtitle: "Personal Information",
          details: "Name, email address, phone number, postal address, date of birth, and gender as provided during account registration."
        },
        {
          subtitle: "Business Information (For Designers)",
          details: "Business name, PAN number, GST identification number (if applicable), bank account details (encrypted), IFSC code, and business address for KYC verification and payment processing."
        },
        {
          subtitle: "Design & Product Data",
          details: "Product images, descriptions, pricing, fabric details, customization options, and other content you upload to showcase your designs."
        },
        {
          subtitle: "Transaction Information",
          details: "Order history, payment details (processed via Razorpay), billing information, shipping addresses, and transaction IDs."
        },
        {
          subtitle: "Usage Data",
          details: "Device information, IP address, browser type, pages visited, time stamps, and other analytics data to improve our services."
        },
        {
          subtitle: "Communication Data",
          details: "Messages, emails, support tickets, and other communications between users and our platform."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          subtitle: "Service Provision",
          details: "To create and manage your account, process orders, facilitate payments, and provide customer support."
        },
        {
          subtitle: "Business Operations",
          details: "To verify designer identities through KYC processes, process payouts (97% after 3% platform commission), generate invoices, and manage marketplace operations."
        },
        {
          subtitle: "Platform Enhancement",
          details: "To personalize your experience, recommend relevant content, analyze usage patterns, and improve our website builder and template systems."
        },
        {
          subtitle: "Legal Compliance",
          details: "To comply with applicable laws including the Information Technology Act, 2000, GST regulations, and RBI guidelines for digital payments."
        },
        {
          subtitle: "Communication",
          details: "To send order confirmations, payment notifications, promotional offers (with consent), and important service updates via email or SMS."
        },
        {
          subtitle: "Fraud Prevention",
          details: "To detect and prevent fraudulent activities, unauthorized access, and ensure platform security through automated monitoring systems."
        }
      ]
    },
    {
      title: "Information Sharing & Disclosure",
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          subtitle: "With Designers & Customers",
          details: "Order details, customer information (name, address, phone) are shared with designers to fulfill orders. Designer profiles and product information are visible to potential customers."
        },
        {
          subtitle: "Payment Processors",
          details: "We share necessary payment information with Razorpay for payment processing, KYC verification, and payout transfers in accordance with RBI regulations."
        },
        {
          subtitle: "Service Providers",
          details: "Trusted third-party service providers who assist with hosting, email delivery, analytics, and technical support under strict confidentiality agreements."
        },
        {
          subtitle: "Legal Requirements",
          details: "When required by law, court orders, government requests, or to protect our rights, users' safety, or prevent illegal activities."
        },
        {
          subtitle: "Business Transfers",
          details: "In case of merger, acquisition, or sale of assets, your information may be transferred subject to the same privacy protections."
        }
      ]
    },
    {
      title: "Data Security & Protection",
      icon: <Lock className="w-5 h-5" />,
      content: [
        {
          subtitle: "Encryption",
          details: "All sensitive data including bank account details are encrypted using industry-standard encryption algorithms (Fernet encryption) and stored securely."
        },
        {
          subtitle: "Access Controls",
          details: "Strict access controls ensure only authorized personnel can access your data on a need-to-know basis for legitimate business purposes."
        },
        {
          subtitle: "Secure Transmission",
          details: "All data transmission between your device and our servers is protected using SSL/TLS encryption (HTTPS protocol)."
        },
        {
          subtitle: "Regular Audits",
          details: "We conduct regular security audits, vulnerability assessments, and maintain up-to-date security patches to protect against threats."
        },
        {
          subtitle: "Payment Security",
          details: "Payment processing follows PCI DSS compliance standards through our payment partner Razorpay, ensuring your financial information is never stored on our servers."
        }
      ]
    },
    {
      title: "Your Rights & Choices",
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          subtitle: "Access & Correction",
          details: "You have the right to access, update, or correct your personal information through your account dashboard or by contacting our support team."
        },
        {
          subtitle: "Data Portability",
          details: "You can request a copy of your data in a structured, machine-readable format for personal use or transfer to another service."
        },
        {
          subtitle: "Account Deletion",
          details: "You may request account deactivation or deletion. However, certain information may be retained for legal compliance, dispute resolution, or fraud prevention."
        },
        {
          subtitle: "Marketing Communications",
          details: "You can opt-out of promotional emails and SMS by using unsubscribe links or updating your communication preferences in account settings."
        },
        {
          subtitle: "Cookie Controls",
          details: "You can manage cookie preferences through your browser settings, though this may affect certain website functionalities."
        }
      ]
    },
    {
      title: "Data Retention",
      icon: <Database className="w-5 h-5" />,
      content: [
        {
          subtitle: "Account Information",
          details: "Personal and business information is retained as long as your account is active and for 7 years after account closure for legal and tax compliance."
        },
        {
          subtitle: "Transaction Records",
          details: "Order, payment, and invoice records are retained for 7 years as required by Indian accounting and tax laws."
        },
        {
          subtitle: "Design Content",
          details: "Uploaded designs and product information are retained until you delete them or close your account, after which they are permanently removed within 30 days."
        },
        {
          subtitle: "Communication Records",
          details: "Support communications are retained for 3 years to ensure service quality and dispute resolution."
        }
      ]
    }
  ];

  return (
    <div>
      <Top/>
      {/* Privacy Policy Section */}
      <section className="py-16 px-4 mt-10 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f4ece5] to-[#f8f3ed]">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#ca7751] rounded-full mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At Vasstr, we are committed to protecting your privacy and ensuring the security of your personal information. This privacy policy explains how we collect, use, and safeguard your data in compliance with Indian laws.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              {/* <p><strong>Effective Date:</strong> December 15, 2025</p>
              <p><strong>Last Updated:</strong> December 15, 2025</p> */}
            </div>
          </div>

          {/* Introduction */}
          <div className="bg-white p-8 rounded-xl shadow-lg mb-8 border-l-4 border-[#ca7751]">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Policy</h2>
            <p className="text-gray-600 mb-4">
              This Privacy Policy applies to the Vasstr platform ("we," "us," or "our") that connects fashion designers with customers through our digital marketplace. We operate in compliance with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and other applicable Indian laws.
            </p>
            <p className="text-gray-600">
              By using our platform, you consent to the collection, use, and sharing of your information as described in this policy. If you do not agree with this policy, please do not use our services.
            </p>
          </div>

          {/* Expandable Sections */}
          <div className="space-y-4">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-[#ca7751]">
                      {section.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  {expandedSection === index ? (
                    <ChevronDown className="w-6 h-6 text-[#ca7751]" />
                  ) : (
                    <ChevronRight className="w-6 h-6 text-[#ca7751]" />
                  )}
                </button>

                {expandedSection === index && (
                  <div className="px-8 pb-8">
                    <div className="space-y-6">
                      {section.content.map((item, itemIndex) => (
                        <div key={itemIndex} className="border-l-2 border-[#ca7751] pl-6">
                          <h4 className="font-semibold text-gray-800 mb-2">{item.subtitle}</h4>
                          <p className="text-gray-600 leading-relaxed">{item.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Cookies & Tracking */}
          <div className="bg-white p-8 rounded-xl shadow-lg mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Database className="w-6 h-6 text-[#ca7751] mr-3" />
              Cookies & Tracking Technologies
            </h2>
            <div className="space-y-4 text-gray-600">
              <p>We use cookies and similar tracking technologies to enhance your experience:</p>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800">Essential Cookies</h4>
                  <p className="text-sm">Required for authentication, security, and basic website functionality.</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800">Analytics Cookies</h4>
                  <p className="text-sm">Help us understand how users interact with our platform to improve services.</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800">Functional Cookies</h4>
                  <p className="text-sm">Remember your preferences and provide personalized features.</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800">Marketing Cookies</h4>
                  <p className="text-sm">Used to deliver relevant advertisements (only with your consent).</p>
                </div>
              </div>
            </div>
          </div>

          {/* International Transfers */}
          <div className="bg-white p-8 rounded-xl shadow-lg mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">International Data Transfers</h2>
            <p className="text-gray-600 mb-4">
              Your data is primarily stored and processed within India. However, some of our service providers may process data outside India. When this occurs, we ensure adequate safeguards are in place through:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Contractual data protection clauses</li>
              <li>Adherence to international data protection standards</li>
              <li>Regular audits of third-party service providers</li>
              <li>Encryption of data in transit and at rest</li>
            </ul>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-[#ca7751] to-[#d67e5a] p-8 rounded-xl shadow-lg mt-8 text-white">
            <h2 className="text-2xl font-bold mb-6">Contact Us</h2>
            <p className="mb-6">If you have any questions about this Privacy Policy or our data practices, please contact us:</p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 mt-1" />
                <div>
                  <h4 className="font-semibold">Email</h4>
                  <p className="text-white/90">team@vasstr.com</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 mt-1" />
                <div>
                  <h4 className="font-semibold">Phone</h4>
                  <p className="text-white/90">+91 9704387878</p>
                  <p className="text-sm text-white/80">(Mon-Sat, 9 AM - 7 PM IST)</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/20">
              <h4 className="font-semibold mb-2">Registered Office</h4>
              <p className="text-white/90">
                Vasstr<br />
                [Your Registered Address]<br />
                Hyderabad, Telangana, India<br />
                PIN: [Your PIN Code]
              </p>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="bg-gray-50 p-6 rounded-xl mt-8 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3">Important Legal Notice</h3>
            <p className="text-sm text-gray-600">
              This Privacy Policy is governed by the laws of India. Any disputes arising from this policy or our privacy practices shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana. We reserve the right to update this policy at any time, and will notify users of significant changes via email or platform notifications.
            </p>
          </div>
        </div>
      </section>
      <VFooter/>
    </div>
  )
}
